/**
 * Build calendar export payloads (.ics + Google Calendar URL) from a CRM
 * appointment. All times go out as UTC `YYYYMMDDTHHMMSSZ` — the user's
 * calendar app renders them in their own timezone, so we don't need any
 * Vienna-aware conversion here. Pure functions, no side effects.
 *
 * .ics RFC 5545 format with VALARM (15-min reminder) so the meeting actually
 * surfaces on the user's phone before it starts. Lines are CRLF + folded at
 * 75 octets per the spec; some clients (notably older Outlook) reject if not.
 */

export interface CalendarExportInput {
  /** Stable id of the appointment — used as the iCal UID (.ics dedups). */
  id: string;
  /** Start time as epoch ms (UTC). */
  startMs: number;
  /** Duration in minutes. Defaults to 60. */
  durationMin?: number;
  /** Title shown in the calendar entry. */
  title: string;
  /** Optional location string (street + city). */
  location?: string | null;
  /** Optional plain-text description. */
  description?: string | null;
  /** Reminder offset in minutes before start. Defaults to 15. */
  reminderMin?: number;
}

const PRODID = '-//4M Immobilien//CRM//DE';

/** Format an epoch ms as RFC 5545 UTC timestamp (`20260510T100000Z`). */
function toIcsUtc(ms: number): string {
  const d = new Date(ms);
  const yyyy = d.getUTCFullYear().toString().padStart(4, '0');
  const mm = (d.getUTCMonth() + 1).toString().padStart(2, '0');
  const dd = d.getUTCDate().toString().padStart(2, '0');
  const hh = d.getUTCHours().toString().padStart(2, '0');
  const mi = d.getUTCMinutes().toString().padStart(2, '0');
  const ss = d.getUTCSeconds().toString().padStart(2, '0');
  return `${yyyy}${mm}${dd}T${hh}${mi}${ss}Z`;
}

/** RFC 5545 escapes: backslash, comma, semicolon, newline → \\, \,, \;, \n. */
function escapeIcsText(input: string): string {
  return input
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

/** Fold long content lines at 75 octets so legacy parsers don't reject them. */
function foldLine(line: string): string {
  if (line.length <= 75) return line;
  const chunks: string[] = [];
  let rest = line;
  chunks.push(rest.slice(0, 75));
  rest = rest.slice(75);
  while (rest.length > 74) {
    chunks.push(' ' + rest.slice(0, 74));
    rest = rest.slice(74);
  }
  if (rest.length > 0) chunks.push(' ' + rest);
  return chunks.join('\r\n');
}

export function buildIcs(input: CalendarExportInput): string {
  const duration = input.durationMin ?? 60;
  const reminder = input.reminderMin ?? 15;
  const startMs = input.startMs;
  const endMs = startMs + duration * 60_000;

  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    `PRODID:${PRODID}`,
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:appointment-${input.id}@4m-immo.com`,
    `DTSTAMP:${toIcsUtc(Date.now())}`,
    `DTSTART:${toIcsUtc(startMs)}`,
    `DTEND:${toIcsUtc(endMs)}`,
    `SUMMARY:${escapeIcsText(input.title)}`,
  ];
  if (input.location) lines.push(`LOCATION:${escapeIcsText(input.location)}`);
  if (input.description) lines.push(`DESCRIPTION:${escapeIcsText(input.description)}`);
  // VALARM = 15-min display reminder (mobile push hooks into this).
  lines.push(
    'BEGIN:VALARM',
    `TRIGGER:-PT${reminder}M`,
    'ACTION:DISPLAY',
    'DESCRIPTION:Erinnerung',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  );
  // RFC 5545 mandates CRLF + line folding.
  return lines.map(foldLine).join('\r\n') + '\r\n';
}

export function googleCalUrl(input: CalendarExportInput): string {
  const duration = input.durationMin ?? 60;
  const startMs = input.startMs;
  const endMs = startMs + duration * 60_000;

  const params = new URLSearchParams();
  params.set('action', 'TEMPLATE');
  params.set('text', input.title);
  params.set('dates', `${toIcsUtc(startMs)}/${toIcsUtc(endMs)}`);
  if (input.description) params.set('details', input.description);
  if (input.location) params.set('location', input.location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/** Trigger a browser download for an .ics blob. */
export function downloadIcs(filename: string, ics: string): void {
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.ics') ? filename : `${filename}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so Safari has time to read the blob.
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}
